import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import EducationIcon from '@mui/icons-material/School';
import StarIcon from '@mui/icons-material/Star';
import SectionCard from './SectionCard';
import ConfirmDialog from '../common/ConfirmDialog';
import { EDUCATION_LEVELS } from './profileConstants';
import { formatUtcDate } from '../../utils/dateTime';
import userService, {
  type CreateUserEducationDto,
  type UpdateUserEducationDto,
  type UserEducationDto,
} from '../../services/userService';

interface EducationCardProps {
  notify: (message: string) => void;
}

/** Education record list with add/edit dialog and delete confirmation. */
export default function EducationCard({ notify }: EducationCardProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [education, setEducation] = useState<UserEducationDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<UserEducationDto | null>(null);
  const [form, setForm] = useState<CreateUserEducationDto>({
    educationLevel: '',
    institutionName: '',
    major: '',
    isPrimary: false,
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const [deleteTarget, setDeleteTarget] = useState<UserEducationDto | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    userService.getEducation()
      .then(setEducation)
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = () => {
    setEditing(null);
    setForm({
      educationLevel: '',
      institutionName: '',
      major: '',
      isPrimary: education.length === 0, // First record is primary by default
    });
    setSaveError('');
    setDialogOpen(true);
  };

  const handleEdit = (edu: UserEducationDto) => {
    setEditing(edu);
    setForm({
      educationLevel: edu.educationLevel,
      institutionName: edu.institutionName ?? '',
      major: edu.major ?? '',
      minor: edu.minor ?? '',
      graduationDate: edu.graduationDate ?? undefined,
      gpa: edu.gpa ?? undefined,
      isPrimary: edu.isPrimary,
    });
    setSaveError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.educationLevel) {
      setSaveError('Education level is required');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      if (editing) {
        const updated = await userService.updateEducation(editing.id, form as UpdateUserEducationDto);
        setEducation(prev => prev.map(e => (e.id === updated.id ? updated : e)));
        notify('Education updated');
      } else {
        const created = await userService.createEducation(form);
        setEducation(prev => [...prev, created]);
        notify('Education added');
      }
      setDialogOpen(false);
    } catch {
      setSaveError('Failed to save education. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await userService.deleteEducation(deleteTarget.id);
      setEducation(prev => prev.filter(e => e.id !== deleteTarget.id));
      setDeleteTarget(null);
      notify('Education record deleted');
    } catch {
      setDeleteError('Failed to delete education record. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <SectionCard
      icon={<EducationIcon />}
      title="Education"
      subtitle="Track your educational background and qualifications."
      action={
        <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAdd}>
          Add Education
        </Button>
      }
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : loadError ? (
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}
        >
          Failed to load your education records.
        </Alert>
      ) : education.length === 0 ? (
        <Alert severity="info">No education records added yet. Click "Add Education" to get started.</Alert>
      ) : (
        <Stack spacing={2}>
          {education.map(edu => (
            <Card key={edu.id} variant="outlined" sx={{ borderRadius: '8px' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {edu.educationLevel}
                      </Typography>
                      {edu.isPrimary && (
                        <Chip icon={<StarIcon />} label="Primary" color="primary" size="small" variant="outlined" />
                      )}
                    </Box>
                    {edu.institutionName && (
                      <Typography variant="body2" color="text.secondary">
                        {edu.institutionName}
                      </Typography>
                    )}
                    {edu.major && (
                      <Typography variant="body2" color="text.secondary">
                        Major: {edu.major}{edu.minor && ` • Minor: ${edu.minor}`}
                      </Typography>
                    )}
                    {(edu.graduationDate || edu.gpa) && (
                      <Typography variant="caption" color="text.secondary">
                        {edu.graduationDate && `Graduated: ${formatUtcDate(edu.graduationDate)}`}
                        {edu.graduationDate && edu.gpa && ' • '}
                        {edu.gpa && `GPA: ${edu.gpa}`}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton size="small" aria-label="Edit education" onClick={() => handleEdit(edu)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      aria-label="Delete education"
                      onClick={() => { setDeleteError(''); setDeleteTarget(edu); }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>{editing ? 'Edit Education' : 'Add Education'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {saveError && <Alert severity="error">{saveError}</Alert>}
            <TextField
              select
              label="Education Level *"
              value={form.educationLevel}
              onChange={e => setForm(prev => ({ ...prev, educationLevel: e.target.value }))}
              fullWidth
              required
            >
              {EDUCATION_LEVELS.map(level => (
                <MenuItem key={level} value={level}>{level}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Institution Name"
              value={form.institutionName || ''}
              onChange={e => setForm(prev => ({ ...prev, institutionName: e.target.value }))}
              fullWidth
              placeholder="e.g. University of Pennsylvania"
            />
            <TextField
              label="Major / Field of Study"
              value={form.major || ''}
              onChange={e => setForm(prev => ({ ...prev, major: e.target.value }))}
              fullWidth
              placeholder="e.g. Accounting"
            />
            <TextField
              label="Minor (Optional)"
              value={form.minor || ''}
              onChange={e => setForm(prev => ({ ...prev, minor: e.target.value }))}
              fullWidth
              placeholder="e.g. Finance"
            />
            <TextField
              label="Graduation Date"
              type="date"
              value={form.graduationDate?.toString().substring(0, 10) || ''}
              onChange={e => setForm(prev => ({ ...prev, graduationDate: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="GPA (Optional)"
              type="number"
              value={form.gpa || ''}
              onChange={e => setForm(prev => ({ ...prev, gpa: e.target.value ? parseFloat(e.target.value) : undefined }))}
              fullWidth
              inputProps={{ min: 0, max: 4, step: 0.01 }}
              placeholder="e.g. 3.75"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.isPrimary}
                  onChange={e => setForm(prev => ({ ...prev, isPrimary: e.target.checked }))}
                  color="primary"
                />
              }
              label="Set as Primary Education"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !form.educationLevel}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          >
            {editing ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Education Record"
        message="Are you sure you want to delete this education record? This action cannot be undone."
        loading={deleting}
        error={deleteError || null}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </SectionCard>
  );
}
